import { useCallback, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import OutlinedInput from '@mui/material/OutlinedInput'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useCreate } from '@refinedev/core'

import { chipThumbnailUrl } from '@/lib/images'
import { paths } from '@/paths'
import { dayjs } from '@/lib/dayjs'
import { track } from '@/lib/segment'

export interface PromotionDraftProduct {
	id: number
	name: string
	image: string | null
}

export interface PromotionDraftStore {
	id: number
	name: string
}

export interface PromotionDraftAction {
	product_name: string
	discount_percent: number
	end_date: string
	selected_product_id: number | null
	selected_store_id: number | null
	products: PromotionDraftProduct[]
	stores: PromotionDraftStore[]
	label: string
}

interface PromotionFormWidgetProps {
	draft: PromotionDraftAction
	onCreated?: (promotionId: number) => void
}

export function PromotionFormWidget({ draft, onCreated }: PromotionFormWidgetProps) {
	const { mutate: createPromotion, isLoading } = useCreate()
	const [selectedProductId, setSelectedProductId] = useState<number | null>(
		draft.selected_product_id ?? draft.products[0]?.id ?? null,
	)
	const [discountPercent, setDiscountPercent] = useState(draft.discount_percent)
	const [endDate, setEndDate] = useState(draft.end_date)
	const [selectedStoreId, setSelectedStoreId] = useState<number | null>(draft.selected_store_id)
	const [error, setError] = useState<string | null>(null)
	const [successId, setSuccessId] = useState<number | null>(null)

	const selectedProduct = draft.products.find((p) => p.id === selectedProductId)

	const handleCreate = useCallback(() => {
		if (!selectedProductId || !selectedProduct) {
			setError('Select a product first.')
			return
		}
		setError(null)

		const expiresAt = endDate
			? dayjs(endDate).endOf('day').toISOString()
			: dayjs().add(7, 'day').endOf('day').toISOString()

		createPromotion(
			{
				resource: 'promotions',
				values: {
					source: 'platform',
					name: `${discountPercent}% off ${selectedProduct.name}`,
					type: 'percent',
					amount: discountPercent,
					applies_to_all_stores: selectedStoreId == null,
					applies_to_all_recipes: false,
					store_ids: selectedStoreId != null ? [selectedStoreId] : [],
					recipe_ids: [selectedProductId],
					activated_at: new Date().toISOString(),
					expires_at: expiresAt,
				},
			},
			{
				onSuccess: (data) => {
					const id = Number((data.data as { id?: number })?.id)
					if (Number.isFinite(id)) {
						setSuccessId(id)
						onCreated?.(id)
						void track('AssistantPromotionCreated', { promotionId: id, productId: selectedProductId })
					}
				},
				onError: (err) => {
					setError(err?.message ?? 'Failed to create promotion')
				},
			},
		)
	}, [createPromotion, discountPercent, endDate, onCreated, selectedProduct, selectedProductId, selectedStoreId])

	if (successId != null) {
		return (
			<Stack spacing={1} sx={{ mt: 1 }}>
				<Typography color="success.main" variant="body2">
					Promotion created successfully.
				</Typography>
				<Chip
					clickable
					color="secondary"
					component="a"
					href={paths.dashboard.promotions.preview(successId)}
					label="View promotion"
					size="small"
					sx={{ alignSelf: 'flex-start' }}
				/>
			</Stack>
		)
	}

	if (!draft.products.length) {
		return (
			<Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
				No products found matching &quot;{draft.product_name}&quot;. Try a different name.
			</Typography>
		)
	}

	return (
		<Box
			sx={{
				mt: 1,
				px: 1.25,
				py: 1,
				borderRadius: 1,
				bgcolor: 'action.hover',
				border: '1px solid',
				borderColor: 'divider',
			}}
		>
			<Typography sx={{ fontWeight: 600, mb: 1 }} variant="caption">
				Create promotion
			</Typography>

			<Stack spacing={1.25}>
				<Stack direction="row" flexWrap="wrap" gap={0.75}>
					{draft.products.map((product) => (
						<Chip
							key={product.id}
							clickable
							color={selectedProductId === product.id ? 'primary' : 'default'}
							label={product.name}
							onClick={() => setSelectedProductId(product.id)}
							size="small"
							avatar={
								product.image ? (
									<Box
										alt=""
										component="img"
										src={chipThumbnailUrl(product.image, 20)}
										sx={{ width: 20, height: 20, borderRadius: '50%' }}
									/>
								) : undefined
							}
						/>
					))}
				</Stack>

				<Stack direction="row" flexWrap="wrap" gap={0.5}>
					{[5, 10, 15, 20, 25].map((pct) => (
						<Chip
							key={pct}
							clickable
							color={discountPercent === pct ? 'primary' : 'default'}
							label={`${pct}%`}
							onClick={() => setDiscountPercent(pct)}
							size="small"
						/>
					))}
				</Stack>

				<OutlinedInput
					fullWidth
					onChange={(e) => setEndDate(e.target.value)}
					size="small"
					type="date"
					value={endDate}
				/>

				<Stack direction="row" flexWrap="wrap" gap={0.5}>
					<Chip
						clickable
						color={selectedStoreId == null ? 'primary' : 'default'}
						label="All stores"
						onClick={() => setSelectedStoreId(null)}
						size="small"
					/>
					{draft.stores.slice(0, 12).map((store) => (
						<Chip
							key={store.id}
							clickable
							color={selectedStoreId === store.id ? 'primary' : 'default'}
							label={store.name}
							onClick={() => setSelectedStoreId(store.id)}
							size="small"
						/>
					))}
				</Stack>

				{error ? (
					<Typography color="error" variant="caption">
						{error}
					</Typography>
				) : null}

				<Button
					disabled={isLoading || !selectedProductId}
					onClick={handleCreate}
					size="small"
					startIcon={isLoading ? <CircularProgress color="inherit" size={14} /> : undefined}
					variant="contained"
					sx={{ alignSelf: 'flex-start' }}
				>
					{draft.label}
				</Button>
			</Stack>
		</Box>
	)
}

export function parsePromotionDraftFromToolResult(result: string): PromotionDraftAction | null {
	try {
		const parsed = JSON.parse(result)
		if (!parsed?.__promotion_draft) return null
		return {
			product_name: String(parsed.product_name ?? ''),
			discount_percent: Number(parsed.discount_percent ?? 10),
			end_date: String(parsed.end_date ?? dayjs().add(7, 'day').format('YYYY-MM-DD')),
			selected_product_id: parsed.selected_product_id != null ? Number(parsed.selected_product_id) : null,
			selected_store_id: parsed.selected_store_id != null ? Number(parsed.selected_store_id) : null,
			products: Array.isArray(parsed.products) ? parsed.products : [],
			stores: Array.isArray(parsed.stores) ? parsed.stores : [],
			label: String(parsed.label ?? 'Create promotion'),
		}
	} catch {
		return null
	}
}
